/**
 * DebouncedSearchInput — Input di ricerca con state locale + debounce.
 *
 * Componente condiviso estratto dal pattern P1 già in produzione dentro
 * `RegistroAttivitaTab.jsx:65-111` (18/02/2026). Ora esposto in
 * `components/common/` per riuso trasversale (Fase 2.3 audit — 16/07/2026).
 *
 * COMPORTAMENTO
 * -------------
 * - `local` state → il DOM aggiorna il valore istantaneamente ad ogni
 *   keystroke: nessun lag percepito dall'utente.
 * - `onDebounced(v)` → propagato al parent SOLO dopo `delay` ms di
 *   inattività. Il parent applica il filtro (che tipicamente attraversa
 *   grandi liste) una-sola-volta invece che ad ogni carattere.
 * - Sync-down: se il parent modifica il valore programmaticamente (es.
 *   `resetFilters`), il local state si allinea — MA solo se il cambiamento
 *   è "esterno" (non un eco del nostro stesso debounce).
 * - Cleanup timer su unmount per evitare setState after unmount.
 *
 * USO
 * ---
 *   <DebouncedSearchInput
 *     externalValue={searchQuery}
 *     onDebounced={setSearchQuery}
 *     placeholder="Cerca…"
 *     delay={200}
 *     testId="my-search"
 *   />
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";

export const DebouncedSearchInput = React.memo(function DebouncedSearchInput({
  externalValue,
  onDebounced,
  placeholder,
  delay = 200,
  className,
  style,
  testId,
  ...rest
}) {
  const [local, setLocal] = useState(externalValue || "");
  const timerRef = useRef(null);
  const lastSentRef = useRef(externalValue || "");

  useEffect(() => {
    if (externalValue !== lastSentRef.current) {
      setLocal(externalValue || "");
      lastSentRef.current = externalValue || "";
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    }
  }, [externalValue]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleChange = useCallback((e) => {
    const v = e.target.value;
    setLocal(v);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      lastSentRef.current = v;
      onDebounced(v);
    }, delay);
  }, [delay, onDebounced]);

  return (
    <Input
      value={local}
      onChange={handleChange}
      placeholder={placeholder}
      className={className}
      style={style}
      data-testid={testId}
      {...rest}
    />
  );
});

export default DebouncedSearchInput;
